import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React from "react"
import {
    Modal,
    Pressable,
    StyleSheet,
    useWindowDimensions,
    View,
} from "react-native"
import { IconButton, Switch } from "react-native-paper"
import {
    READING_ADAPTED,
    READING_FONTS,
    READING_SIZES,
    READING_STANDARD,
} from "@data/utility/readingPresets"

/**
 * La fiche « Ma lecture », dans l'ancien design de l'espace élève — à refaire au design system
 * avec l'écran, la feuille de l'énoncé, elle, ne bougera pas. Peu d'options : la lecture adaptée
 * d'un coup, puis la police et la taille.
 *
 * Une `Modal` native plutôt que `BottomSheetModalM` : l'énoncé s'affiche dans `MaterialDisplayModal`,
 * elle-même une `Modal` native, et tout portail (la fiche de `@gorhom`, `ModalM`) s'ouvre sous
 * elle, à la racine. Sur un téléphone,
 * elle monte du bas ; sur un grand écran, une fiche du bas restait étroite et collée au pied de la
 * page : elle s'y ouvre en fenêtre centrée. Tout le fond s'assombrit dans les deux cas.
 *
 * Chaque geste s'applique tout de suite (`onChange` reçoit la lecture complète) : l'énoncé change
 * sous la fiche. Le lecteur n'enregistre qu'à la fermeture (`onClose`), quelle qu'en soit
 * la façon : « C'est bon », la croix, le fond ou le retour.
 */
export default function ReadingSheet(props) {
    const { visible, reading, onChange, onClose } = props
    const { width } = useWindowDimensions()
    const centered = width >= CENTERED_FROM

    const toggleAdapted = () =>
        onChange(reading.adapted ? READING_STANDARD : READING_ADAPTED)
    const pick = (field, value) => onChange({ ...reading, [field]: value })

    return (
        <Modal
            visible={visible}
            transparent
            animationType={centered ? "fade" : "slide"}
            onRequestClose={onClose}
        >
            <View style={[styles.overlay, centered && styles.overlayCentered]}>
                <Pressable
                    style={StyleSheet.absoluteFill}
                    onPress={onClose}
                    accessibilityLabel="Fermer Ma lecture"
                />
                <View
                    style={[styles.sheet, centered && styles.dialog]}
                    accessibilityViewIsModal
                >
                    <View style={styles.header}>
                        <TextM
                            fontWeight="Bold"
                            fontSize={18}
                            color={theme.colors.primary}
                        >
                            Ma lecture
                        </TextM>
                        <IconButton
                            icon="times"
                            size={22}
                            iconColor={theme.colors.primary}
                            onPress={onClose}
                            accessibilityLabel="Fermer"
                        />
                    </View>

                    <View style={styles.adaptedRow}>
                        <View style={{ flex: 1 }}>
                            <TextM fontWeight="Bold" fontSize={15}>
                                Lecture adaptée
                            </TextM>
                            <TextM fontSize={12} color={theme.colors.grey} wrap>
                                Allume d'un coup des réglages pour lire plus
                                facilement.
                            </TextM>
                        </View>
                        <Switch
                            value={reading.adapted}
                            onValueChange={toggleAdapted}
                            color={theme.colors.primary}
                            accessibilityLabel="Lecture adaptée"
                        />
                    </View>

                    <TextM
                        fontWeight="SemiBold"
                        fontSize={13}
                        style={styles.label}
                    >
                        Police
                    </TextM>
                    <Segmented
                        options={READING_FONTS}
                        selected={reading.font}
                        onPick={(value) => pick("font", value)}
                    />

                    <TextM
                        fontWeight="SemiBold"
                        fontSize={13}
                        style={styles.label}
                    >
                        Taille du texte
                    </TextM>
                    <Segmented
                        options={READING_SIZES.map((size) => ({
                            ...size,
                            text: "A",
                            fontSize: size.glyph,
                        }))}
                        selected={reading.size}
                        onPick={(value) => pick("size", value)}
                    />

                    <FilledButtonM
                        label="C'est bon"
                        color={theme.colors.primary}
                        onPress={onClose}
                        style={{ marginTop: 20, width: "100%" }}
                    />
                </View>
            </View>
        </Modal>
    )
}

function Segmented({ options, selected, onPick }) {
    return (
        <View style={styles.segmented}>
            {options.map((option, index) => {
                const isSelected = option.value === selected
                return (
                    <Pressable
                        key={option.value}
                        onPress={() => onPick(option.value)}
                        accessibilityRole="button"
                        accessibilityLabel={option.label}
                        accessibilityState={{ selected: isSelected }}
                        style={[
                            styles.segment,
                            index > 0 && styles.segmentDivider,
                            isSelected && {
                                backgroundColor: theme.colors.primaryLighter,
                            },
                        ]}
                    >
                        <TextM
                            fontWeight={isSelected ? "Bold" : "Medium"}
                            fontSize={option.fontSize ?? 13}
                            color={
                                isSelected
                                    ? theme.colors.primary
                                    : theme.colors.black
                            }
                        >
                            {option.text ?? option.label}
                        </TextM>
                    </Pressable>
                )
            })}
        </View>
    )
}

/** À partir de cette largeur, la fiche s'ouvre en fenêtre centrée plutôt que depuis le bas. */
const CENTERED_FROM = 600

const styles = {
    overlay: {
        flex: 1,
        justifyContent: "flex-end",
        backgroundColor: "rgba(0,0,0,0.5)",
    },
    overlayCentered: {
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
    },
    sheet: {
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
        paddingTop: 8,
        paddingBottom: 28,
        width: "100%",
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
    },
    dialog: { maxWidth: 480, borderRadius: 16, paddingBottom: 20 },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    adaptedRow: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.primaryLighter,
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.primary,
        borderRadius: 8,
        paddingVertical: 10,
        paddingLeft: 12,
        paddingRight: 6,
    },
    label: { marginTop: 12, marginBottom: 6 },
    segmented: {
        flexDirection: "row",
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 20,
        overflow: "hidden",
    },
    segment: {
        flex: 1,
        minHeight: 44,
        alignItems: "center",
        justifyContent: "center",
    },
    segmentDivider: {
        borderLeftWidth: 1,
        borderLeftColor: theme.colors.lightMore,
    },
}
